import { type ReactNode, useId, useState } from "react";
import {
  CheckIcon,
  ChevronDownIcon,
  LinkIcon,
  LogOutIcon,
  MoonIcon,
  NavGlyph,
  ScopeIcon,
  SunIcon,
  WorkspaceIcon,
} from "./icons.tsx";
import type { RailWorkspace } from "./rail.tsx";
import { INBOX_SECTION, mailGroups, type NavRow, type ShellNav } from "./shell-nav.ts";
import { Tray, TrayChoices } from "./tray.tsx";

export type PhoneMenuProps = {
  user: { name: string; email: string };
  workspaces: readonly RailWorkspace[];
  current: RailWorkspace | null;
  /** The same rows the rail draws (`shell-nav.ts`); null while there is no workspace. */
  nav: ShellNav | null;
  dark: boolean;
  onToggleTheme: () => void;
  /** Follows a link in place; the caller decides whether unsaved work allows it. */
  onNavigate: (href: string) => void;
  onWorkspace: (id: string) => void;
  onSignOut: () => Promise<void>;
  /** Rows for what the reader sees in this workspace, in their own section after the device's. */
  displayOptions?: ReactNode;
};

export const initials = (name: string): string =>
  name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("");

/** One place in the menu sheet: a full-width row, the current one ticked. */
function PlaceRow(props: { row: NavRow; onFollow: (href: string) => void }) {
  const { row } = props;
  // A real 0 is shown, as the rail shows it (LEA-501); only a row with no total has none.
  const count = row.count === null ? null : row.count.toLocaleString("en-GB");
  return (
    <a
      className="lcr-tray-row"
      href={row.href}
      aria-current={row.current ? "page" : undefined}
      data-sub={row.sub || undefined}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        props.onFollow(row.href);
      }}
    >
      <NavGlyph icon={row.icon} size={20} />
      <span className="lcr-tray-row-text">{row.label}</span>
      {count && (
        <span className="lcr-tray-row-count">
          <span aria-hidden="true">{count}</span>
          <span className="lcr-visually-hidden">, {count} conversations</span>
        </span>
      )}
      {!count && row.unknown && (
        <span className="lcr-tray-row-count" data-unknown="">
          <span aria-hidden="true">–</span>
          <span className="lcr-visually-hidden">, count unavailable</span>
        </span>
      )}
      {/* The tick's room is kept on every row, so the counts stand in one column. */}
      <span className="lcr-tray-tick" aria-hidden="true">
        {row.current && <CheckIcon size={18} />}
      </span>
    </a>
  );
}

/**
 * One group of places, as the rail parts them: Inbox and the work, the label views under "Labels",
 * the boxes, then this person's saved views under "Views" and the pages. The tray's own gap parts
 * the groups.
 */
function PlaceList(props: {
  label?: string;
  rows: readonly NavRow[];
  onFollow: (href: string) => void;
}) {
  const id = useId();
  return (
    <section className="lcr-tray-section" aria-labelledby={props.label ? id : undefined}>
      {props.label && (
        <h3 id={id} className="lcr-tray-label">
          {props.label}
        </h3>
      )}
      <div className="lcr-tray-list">
        {props.rows.map((row) => (
          <PlaceRow key={row.key} row={row} onFollow={props.onFollow} />
        ))}
      </div>
    </section>
  );
}

/*
 * The phone top bar keeps the brand and one control. That control names the workspace you are in and
 * opens everything the rail spreads out: the same places, the workspace choice, the theme and the
 * account.
 */
export function PhoneMenu(props: PhoneMenuProps) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const close = () => setOpen(false);
  const follow = (href: string) => {
    close();
    props.onNavigate(href);
  };
  const { nav } = props;
  // The sections lead, as the rail leads with them; the inbox's own rows follow (LEA-501).
  // The inbox's own Inbox row is that section's home, so the section is not listed twice.
  const sections = nav
    ? [
        ...nav.sections.filter((row) => row.key !== INBOX_SECTION || nav.mail.length === 0),
        ...(nav.settings ? [nav.settings] : []),
      ]
    : [];
  const places = nav ? [...sections, ...nav.mail, ...nav.more, ...nav.views] : [];
  return (
    <>
      <button
        type="button"
        className="lcr-phone-menu-button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Menu, ${props.current?.name ?? "no workspace"}`}
        onClick={() => setOpen(true)}
      >
        {props.current && <span className="lcr-phone-menu-name">{props.current.name}</span>}
        <span className="lcr-phone-menu-avatar" aria-hidden="true">
          {initials(props.user.name)}
        </span>
        <ChevronDownIcon size={14} />
      </button>
      <Tray open={open} title="Menu" onClose={close}>
        <div className="lcr-tray-account">
          <span className="lcr-phone-menu-avatar" aria-hidden="true">
            {initials(props.user.name)}
          </span>
          <span className="lcr-tray-row-text">
            <strong className="lcr-tray-name">{props.user.name}</strong>
            <span>{props.user.email}</span>
          </span>
        </div>
        {nav && places.length > 0 && (
          <nav className="lcr-tray-places" aria-label="Places">
            {sections.length > 0 && <PlaceList rows={sections} onFollow={follow} />}
            {mailGroups(nav.mail).map((group) => (
              <PlaceList
                key={group.key}
                {...(group.label ? { label: group.label } : {})}
                rows={group.rows}
                onFollow={follow}
              />
            ))}
            {nav.views.length > 0 && <PlaceList label="Views" rows={nav.views} onFollow={follow} />}
            {nav.more.length > 0 && <PlaceList label="More" rows={nav.more} onFollow={follow} />}
          </nav>
        )}
        {/* After the places, so twenty colleagues never push Needs reply down the sheet. */}
        {nav?.scope && (
          <TrayChoices
            label="Showing"
            value={nav.scope.value}
            icon={<ScopeIcon size={20} />}
            choices={[...nav.scope.fixed, ...nav.scope.people]}
            onChange={(value) => {
              const href = [...(nav.scope?.fixed ?? []), ...(nav.scope?.people ?? [])].find(
                (choice) => choice.value === value,
              )?.href;
              if (href) follow(href);
            }}
          />
        )}
        {props.current && props.workspaces.length > 1 && (
          <TrayChoices
            label="Workspace"
            value={props.current.id}
            icon={<WorkspaceIcon size={20} />}
            choices={props.workspaces.map((workspace) => ({
              value: workspace.id,
              label: workspace.name,
              ...(workspace.needsReply
                ? { count: { value: workspace.needsReply, noun: "need a reply" } }
                : {}),
            }))}
            onChange={(id) => {
              close();
              if (id !== props.current?.id) props.onWorkspace(id);
            }}
          />
        )}
        {props.displayOptions}
        <section className="lcr-tray-section" aria-label="Your account">
          <a
            className="lcr-tray-row"
            href="/account/connections"
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              follow("/account/connections");
            }}
          >
            <LinkIcon size={20} />
            <span className="lcr-tray-row-text">Connected apps</span>
          </a>
        </section>
        <section className="lcr-tray-section" aria-label="Settings for this device">
          <div className="lcr-tray-list">
            <button
              type="button"
              className="lcr-tray-row"
              role="switch"
              aria-checked={props.dark}
              onClick={props.onToggleTheme}
            >
              {props.dark ? <MoonIcon size={20} /> : <SunIcon size={20} />}
              <span className="lcr-tray-row-text">Dark mode</span>
              <span className="lcr-tray-switch" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="lcr-tray-row"
              disabled={signingOut}
              onClick={async () => {
                setSigningOut(true);
                await props.onSignOut();
              }}
            >
              <LogOutIcon size={20} />
              <span className="lcr-tray-row-text">Sign out</span>
            </button>
          </div>
        </section>
      </Tray>
    </>
  );
}
